import { createStore } from 'jotai';
import {
  diagnosticsModeAtom,
  dodAtom,
  sliceAreaAtom,
  unitAtom,
  zoomAreaAtom,
  logAxisAtom,
  yScaleModeAtom,
  primaryFilterVisibilityAtom,
  layoutAtom,
  percentileAtom,
  widgetPanelVisibilityAtom,
  activeTabAtom as metricPanelActiveTabAtom,
  Tabs as MetricPanelTabs,
} from '@components/metrics-panel-hz/atoms';
import {
  dimensionPanelAtom,
  metricPanelAtom,
  summaryPanelAtom,
  currentKpiIdAtom,
} from '@src/atoms/panels';
import { aiAlertAtom, manualAlertAtom } from '@src/atoms/toggles';
import {
  isTableCompareEnabledAtom,
  dimensionSortsAtom,
  heightAtom,
  activeTabAtom,
} from '@src/components/dimension-panel-hz/atoms/index';
import {
  globalFilterRulesExpandedAtom,
  globalFilterAppendedAtomStatusAtom,
} from '@components/filter/global-atoms';
import { get } from 'lodash';
import { Tabs } from '@src/store/trends/base/reducer';
import { initWidgetStore } from '@src/components/widget-panel/state/dashboard';
import { useCaseKeyAtom } from '@src/atoms/pageMeta';

function initStore(state: any = {}) {
  const store = createStore();
  const isPresetDashboard = 'usecaseKey' in state && !!state.usecaseKey;
  if ('metricPanel' in state) {
    const {
      size = 's',
      unit = '%',
      sliceArea = null,
      zoomArea = null,
      diagnosticsMode = false,
      logAxis = false,
      yScaleMode = false,
      primaryFilterVisibility = true,
      layout = null,
      activeTab = MetricPanelTabs.MetricCharts,
      widgetPanelVisibility = false,
      percentile = 'avg',
    }: any = state.metricPanel;

    store.set(layoutAtom, layout || `4${size}`);
    store.set(unitAtom, unit);
    store.set(percentileAtom, percentile);
    store.set(sliceAreaAtom, sliceArea);
    store.set(zoomAreaAtom, zoomArea);
    store.set(diagnosticsModeAtom, diagnosticsMode);
    store.set(logAxisAtom, logAxis);
    store.set(yScaleModeAtom, yScaleMode);
    store.set(primaryFilterVisibilityAtom, primaryFilterVisibility);
    store.set(metricPanelActiveTabAtom, activeTab);
    store.set(
      widgetPanelVisibilityAtom,
      'widgetPanelVisibility' in state.metricPanel ? widgetPanelVisibility : !isPresetDashboard,
    );
  }

  if ('toggles' in state) {
    const { sections, diagnostics }: any = state.toggles;
    if (sections) {
      const { summary = true, metric = true, dimension = true } = sections;
      store.set(summaryPanelAtom, summary);
      store.set(metricPanelAtom, metric);
      store.set(dimensionPanelAtom, dimension);
    }
    if (diagnostics) {
      const { manualAlert = false, aiAlert = false, dod = false } = diagnostics;
      store.set(manualAlertAtom, manualAlert);
      store.set(aiAlertAtom, aiAlert);
      store.set(dodAtom, dod);
    }
  }

  // this should have been inside dimensionPanel state
  if ('isTableCompareEnabled' in state) {
    const { isTableCompareEnabled = false }: any = state;
    store.set(isTableCompareEnabledAtom, isTableCompareEnabled);
  }

  if ('globalFilter' in state) {
    const { rulesExpanded = false, appendedAtomStatus = 'dimension' }: any = state.globalFilter;
    store.set(globalFilterRulesExpandedAtom, rulesExpanded);
    store.set(globalFilterAppendedAtomStatusAtom, appendedAtomStatus);
  }

  // if ('currentKpiId' in state) {
  //   const { currentKpiId = 1 }: any = state;
  //   store.set(currentKpiIdAtom, currentKpiId);
  // }
  if ('currentKpiIdSelected' in state) {
    const { currentKpiIdSelected = 1 }: any = state;
    store.set(currentKpiIdAtom, currentKpiIdSelected);
  }

  const trendsBase = get(state, ['module:trends', 'base'], {});
  let activeTab = get(trendsBase, ['dimensionPanelActiveTab'], Tabs.TopTrends);
  if ('dimensionPanel' in state) {
    store.set(dimensionSortsAtom, state.dimensionPanel.sorts || {});
    store.set(heightAtom, state.dimensionPanel.height || 's');
    // migrated from redux to atom
    activeTab = state.dimensionPanel.activeTab || activeTab;
  }
  store.set(activeTabAtom, activeTab);

  // widget panel
  const widgetState = get(state, ['widgetState'], null);
  initWidgetStore(widgetState);

  if (isPresetDashboard) {
    store.set(useCaseKeyAtom, state.usecaseKey);
  }

  return store;
}

export default initStore;
